import React, { useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router';
import { Subject } from 'rxjs';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { StickyLayout } from '../Sticky/StickyLayout';
import { Sticky } from '../Sticky/Sticky';
import { LayoutBleed } from '../LayoutBleed/LayoutBleed';
import { Gutter } from '../Gutter/Gutter';

import { PageContext } from './PageContext';

export type PageProps = {
  children: React.ReactNode;
  Header?: React.ReactNode;
  Footer?: React.ReactNode;
  disablePageBleed?: boolean;
  alignment?: 'top' | 'middle';
  name?: string;
};
export const Page = ({
  children,
  disablePageBleed = false,
  alignment = 'top',
  Header,
  Footer,
  name,
}: PageProps) => {
  const { pathname } = useLocation();

  const titleSubject = useMemo(() => new Subject<string>(), []);

  const setTitle = useCallback(
    (newTitle: string) => {
      titleSubject.next(newTitle);
    },
    [titleSubject]
  );

  const contextValue = useMemo(() => ({ setTitle }), [setTitle]);

  useEffect(() => {
    const subscription = titleSubject.subscribe(newTitle => {
      document.title = newTitle;
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [titleSubject]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    setTitle('Axiom Law');
  }, [setTitle]);

  const blockContent = disablePageBleed ? (
    <div className="content-box">{children}</div>
  ) : (
    <LayoutBleed data-test="LAYOUT_BLEED">
      <div className="content-box">{children}</div>
    </LayoutBleed>
  );

  return (
    <PageContext.Provider value={contextValue}>
      <StickyLayout>
        <div
          id="PageBlock"
          data-test={name}
          className={AttrsHelper.formatClassname(
            !!Header && 'has-header',
            alignment === 'middle' && 'alignment-middle'
          )}
        >
          {Header ? <Sticky name="STICKY_HEADER">{Header}</Sticky> : null}
          <div className="page-content">{blockContent}</div>
          {Footer ? (
            <Gutter top="16px" name="FOOTER_GUTTER">
              {Footer}
            </Gutter>
          ) : null}
        </div>
      </StickyLayout>
    </PageContext.Provider>
  );
};
